import React, { createContext, useContext, useMemo, useState } from "react";
import { DocumentCurrencyConfigView } from "@/Contracts";

export interface DocumentCurrencyContextValue {
	currency: string | null;
	frozenRate: number | null;
	frozenAt: string | null;
	isLocked: boolean;
	isFrozen: boolean;
	companyCurrency: string | null;
	skipLiveConfigFetch: boolean;
	isQuoteView: boolean;
	setQuoteView: (next: boolean) => void;
}

const DocumentCurrencyContext =
	createContext<DocumentCurrencyContextValue | null>(null);

interface DocumentCurrencyProviderProps {
	config: DocumentCurrencyConfigView | null;
	companyCurrency?: string | null;
	skipLiveConfigFetch?: boolean;
	children: React.ReactNode;
}

export const DocumentCurrencyProvider: React.FC<
	DocumentCurrencyProviderProps
> = ({
	config,
	companyCurrency = null,
	skipLiveConfigFetch = false,
	children
}) => {
	const [isQuoteView, setQuoteView] = useState(false);

	const value = useMemo<DocumentCurrencyContextValue>(() => {
		if (!config) {
			return {
				currency: null,
				frozenRate: null,
				frozenAt: null,
				isLocked: false,
				isFrozen: false,
				companyCurrency,
				skipLiveConfigFetch,
				isQuoteView,
				setQuoteView
			};
		}
		const frozenRate = config.frozenExchangeRate ?? null;
		const frozenAt = config.frozenAt ?? null;
		const isLocked = frozenAt !== null;
		return {
			currency: config.currency.code,
			frozenRate,
			frozenAt,
			isLocked,
			isFrozen: isLocked,
			companyCurrency,
			skipLiveConfigFetch,
			isQuoteView,
			setQuoteView
		};
	}, [config, companyCurrency, skipLiveConfigFetch, isQuoteView]);

	return (
		<DocumentCurrencyContext.Provider value={value}>
			{children}
		</DocumentCurrencyContext.Provider>
	);
};

/**
 * Returns the document's currency context, or null when called outside a
 * <DocumentCurrencyProvider> (e.g. from the catalog page).
 */
export const useDocumentCurrency = (): DocumentCurrencyContextValue | null =>
	useContext(DocumentCurrencyContext);

export default DocumentCurrencyContext;
